/* eslint-disable react-hooks/exhaustive-deps */
import { Upload, Search, RotateCw, Copy } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Table
} from 'react-bootstrap'
import toast, { Toaster } from 'react-hot-toast'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import Loader from '../../../components/atoms/Loader'
import TableAction from '../../../components/atoms/Table/TableAction'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import InputDatePicker from '../../../components/Input/DatePicker/InputDatePicker'
import ModalUploadSupportingDocument from '../../../components/modal/InboundHistory/ModalUploadSupportingDocument'
import api from '../../../config/AxiosInstance'
import { convertDateWithTime } from '../../../helpers/constants'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

const SelectStyles = {
  control: (base) => ({
    ...base,
    borderRadius: '10px',
    border: '1px solid #e9ecef',
    boxShadow: 'none',
    minHeight: '45px',
    fontSize: '0.9rem',
    '&:hover': {
      border: '1px solid #bdc3c7'
    }
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '10px',
    fontSize: '0.9rem',
    zIndex: 9999
  })
}

const INBOUND_TYPES = [
  { value: 'pr', label: 'Purchase Receipt' },
  { value: 'ml', label: 'Receive ML' },
  { value: 'ir', label: 'Inbound Return' },
  { value: 'mr', label: 'Material Reject' }
]

const InboundHistoryPage = () => {
  const [inboundHistories, setInboundHistories] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [warehouses, setWarehouses] = useState([])
  const { state } = useLocation()
  const [
    showModalUploadDocumentSupporting,
    setShowModalUploadDocumentSupporting
  ] = useState(false)
  const [valueInboundId, setValueInboundId] = useState(null)
  const [metaData, setMetaData] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })
  const { per_page, current_page } = metaData
  const [pageCount, setPageCount] = useState(0)

  const [filter, setFilter] = useState({
    q: state?.q || '',
    warehouse_id: state?.warehouse_id || '',
    type: state?.type || '',
    start_date: state?.start_date || '',
    end_date: state?.end_date || ''
  })
  const [debouncedQ, setDebouncedQ] = useState(filter.q)

  const warehouseOptions = [
    { value: '', label: 'All Warehouses' },
    ...warehouses.map((w) => ({ value: w.id, label: w.warehouse_name }))
  ]

  const typeOptions = [{ value: '', label: 'All Types' }, ...INBOUND_TYPES]

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQ(filter.q)
      if (filter.q !== debouncedQ) {
        setMetaData((prev) => ({ ...prev, current_page: 1 }))
      }
    }, 1000)

    return () => {
      clearTimeout(handler)
    }
  }, [filter.q])

  const handleOnChangeMeta = (e) => {
    setMetaData({
      ...metaData,
      [e.target.name]: Number(e.target.value),
      current_page: 1
    })
  }

  const handleFilterChange = (name, value) => {
    let newFilter = {
      ...filter,
      [name]: value
    }

    if (name === 'start_date' && value && filter.end_date) {
      if (new Date(value) > new Date(filter.end_date)) {
        newFilter.end_date = value
      }
    }

    if (name === 'end_date' && value && filter.start_date) {
      if (new Date(value) < new Date(filter.start_date)) {
        newFilter.start_date = value
      }
    }

    setFilter(newFilter)

    if (name !== 'q') {
      setMetaData((prev) => ({ ...prev, current_page: 1 }))
    }
  }

  const handlePageChange = (data) => {
    const selectedPage = data.selected + 1
    setMetaData((prevMetaData) => ({
      ...prevMetaData,
      current_page: selectedPage
    }))
    window.scrollTo(0, 0)
  }

  const handleCloseModalUploadDocument = () => {
    setShowModalUploadDocumentSupporting(false)
    setValueInboundId(null)
  }

  const handleShowModalModalUploadDocument = (id) => {
    setShowModalUploadDocumentSupporting(true)
    setValueInboundId(id)
  }

  const handleResetFilter = () => {
    setFilter({
      q: '',
      warehouse_id: '',
      type: '',
      start_date: '',
      end_date: ''
    })
    setDebouncedQ('')
    setMetaData((prev) => ({ ...prev, current_page: 1 }))
  }

  const fetchListInboundHistories = async (page = null) => {
    try {
      setIsLoading(true)
      const response = await api.get('/inbound/history', {
        params: {
          per_page,
          page: page ?? current_page,
          q: debouncedQ === '' ? null : debouncedQ,
          warehouse_id: filter.warehouse_id,
          type: filter.type,
          start_date: filter.start_date
            ? convertDateWithTime(filter.start_date, 'YYYY-MM-DD')
            : '',
          end_date: filter.end_date
            ? convertDateWithTime(filter.end_date, 'YYYY-MM-DD')
            : ''
        }
      })
      const { metadata, data } = response.data
      setMetaData(metadata)
      setInboundHistories(data)
      setPageCount(metadata.total_page || 0)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'

      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchListInboundHistories()
  }, [
    per_page,
    current_page,
    debouncedQ,
    filter.warehouse_id,
    filter.type,
    filter.start_date,
    filter.end_date
  ])

  const fetchWarehouses = async () => {
    try {
      const params = {
        is_parent: 1,
        forUser: 1
      }
      const response = await api.get('/master/warehouses/all', { params })
      const { data } = response.data
      setWarehouses(data)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    }
  }

  useEffect(() => {
    fetchWarehouses()
  }, [])

  return (
    <>
      <div className="container px-4">
        <Row className="mb-4 align-items-center">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <h3 style={headerTitleStyle}>Inbound History</h3>
              <div className="d-flex gap-2">
                <Button
                  variant="light"
                  className="text-secondary fw-bold rounded px-3 border"
                  onClick={handleResetFilter}
                  style={{ fontSize: '0.85rem' }}
                >
                  <RotateCw size={14} className="me-2" />
                  Reset Filters
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        <Card style={cardFilterStyle}>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label style={labelStyle}>
                  Search Inbound Document
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text
                    style={{
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRight: 'none',
                      borderTopLeftRadius: '10px',
                      borderBottomLeftRadius: '10px'
                    }}
                  >
                    <Search size={16} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search Inb Doc or Erp Doc"
                    onChange={(e) => handleFilterChange('q', e.target.value)}
                    value={filter.q}
                    style={{
                      ...FormControlStyle,
                      borderLeft: 'none',
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0
                    }}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={labelStyle}>Warehouse</Form.Label>
                <Select
                  value={warehouseOptions.find(
                    (o) => o.value === filter.warehouse_id
                  )}
                  onChange={(e) =>
                    handleFilterChange('warehouse_id', e?.value || '')
                  }
                  options={warehouseOptions}
                  styles={SelectStyles}
                  placeholder="All Warehouses"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={labelStyle}>Type</Form.Label>
                <Select
                  value={typeOptions.find((o) => o.value === filter.type)}
                  onChange={(e) => handleFilterChange('type', e?.value || '')}
                  options={typeOptions}
                  styles={SelectStyles}
                  placeholder="All Types"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={labelStyle}>Start Date</Form.Label>
                <div
                  style={FormControlStyle}
                  className="d-flex align-items-center px-2"
                >
                  <InputDatePicker
                    value={filter.start_date}
                    onChange={(date) => handleFilterChange('start_date', date)}
                    maxDate={filter.end_date ? new Date(filter.end_date) : null}
                    clearIcon
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={labelStyle}>End Date</Form.Label>
                <div
                  style={FormControlStyle}
                  className="d-flex align-items-center px-2"
                >
                  <InputDatePicker
                    value={filter.end_date}
                    onChange={(date) => handleFilterChange('end_date', date)}
                    minDate={
                      filter.start_date ? new Date(filter.start_date) : null
                    }
                    clearIcon
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Card>

        <div className="row mt-3">
          <TableData
            isLoading={isLoading}
            inboundHistories={inboundHistories}
            page={metaData}
            pageCount={pageCount}
            onHandleOnChangeMeta={handleOnChangeMeta}
            onHandlePageChange={handlePageChange}
            onHandleShowModalUploadDocument={handleShowModalModalUploadDocument}
            stateValue={filter}
          />
        </div>
      </div>
      <ModalUploadSupportingDocument
        show={showModalUploadDocumentSupporting}
        onHide={() => handleCloseModalUploadDocument()}
        valueInboundId={valueInboundId}
        onRefreshData={() => fetchListInboundHistories(per_page, current_page)}
      />
      <Toaster />
    </>
  )
}

function TableData({
  isLoading,
  inboundHistories,
  page,
  onHandleOnChangeMeta,
  onHandlePageChange,
  onHandleShowModalUploadDocument,
  stateValue
}) {
  return (
    <>
      <Table
        hover
        responsive
        className="shadow-sm mb-5 bg-white"
        style={{
          borderRadius: '12px',
          overflow: 'hidden',
          fontSize: '0.9rem'
        }}
      >
        <thead style={{ backgroundColor: '#f8f9fa' }}>
          <tr>
            <th className="p-2 align-middle" style={tableHeaderStyle}>
              Type
            </th>
            <th className="p-2 align-middle" style={tableHeaderStyle}>
              Warehouse
            </th>
            <th className="p-2 align-middle text-end" style={tableHeaderStyle}>
              ERP Doc No.
            </th>
            <th className="p-2 align-middle" style={tableHeaderStyle}>
              Inb Doc No.
            </th>
            <th className="p-2 align-middle" style={tableHeaderStyle}>
              Supporting Document
            </th>
            <th className="p-2 align-middle text-end" style={tableHeaderStyle}>
              Submit
            </th>
            <th
              className="p-2 align-middle text-end"
              width="15%"
              style={tableHeaderStyle}
            >
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={7} className="text-center p-5">
                <Loader />
              </td>
            </tr>
          )}
          {!isLoading && inboundHistories.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center fw-bold p-5 text-muted">
                No Data Found
              </td>
            </tr>
          )}
          {!isLoading &&
            inboundHistories.length > 0 &&
            inboundHistories.map((value, index) => (
              <RowData
                key={index}
                data={value}
                onHandleShowModalUploadDocument={
                  onHandleShowModalUploadDocument
                }
                stateValue={stateValue}
              />
            ))}
        </tbody>
        <tfoot>
          <tr>
            <td className="p-3 border-top-0" colSpan={7}>
              <TablePagination
                meta={page}
                handleOnChangeMeta={onHandleOnChangeMeta}
                handlePageChange={onHandlePageChange}
              />
            </td>
          </tr>
        </tfoot>
      </Table>
    </>
  )
}

function RowData({ data, onHandleShowModalUploadDocument, stateValue }) {
  const navigate = useNavigate()
  const fileName = data.support_doc ? data.support_doc.split('/').pop() : '-'

  const urlDetail = (type, prID, mlID, irID, mrID) => {
    let url = ''
    if (type === 'pr') {
      url = `/inbound/purchase-receipt/${prID}`
    }
    if (type === 'ml') {
      url = `/inbound/ml/detail/${mlID}`
    }
    if (type === 'ir') {
      url = `/inbound/inbound-return/detail/${irID}`
    }
    if (type === 'mr') {
      url = `/inbound/material-reject/detail/${mrID}`
    }
    return url
  }

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard!', {
      position: 'top-right',
      style: {
        fontSize: '14px',
        fontWeight: '600'
      }
    })
  }

  return (
    <tr key={data.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
      <td className="p-2 align-middle text-uppercase">{data.inbound_type}</td>
      <td className="p-2 align-middle text-muted">
        {data?.warehouse_name || '-'}
      </td>
      <td className="p-2 align-middle text-end text-muted">
        {data.erp_code ? (
          <div className="d-flex align-items-center justify-content-end gap-2">
            {data.erp_code}
            <Copy
              size={14}
              className="text-muted cursor-pointer"
              style={{
                cursor: 'pointer',
                opacity: 0.5,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}
              onClick={() => handleCopy(data.erp_code)}
              title="Copy ERP Doc No."
            />
          </div>
        ) : (
          '-'
        )}
      </td>
      <td className="p-2 align-middle fw-semibold text-primary">
        {data?.inbound_code ? (
          <div className="d-flex align-items-center gap-2">
            {data.inbound_code}
            <Copy
              size={14}
              className="text-muted cursor-pointer"
              style={{
                cursor: 'pointer',
                opacity: 0.5,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}
              onClick={() => handleCopy(data.inbound_code)}
              title="Copy Inb Doc No."
            />
          </div>
        ) : (
          '-'
        )}
      </td>
      <td className="p-2 align-middle">
        {!data.support_doc && '-'}
        {data.support_doc && (
          <Link to={data.support_doc} className="text-decoration-none">
            {fileName}
          </Link>
        )}
      </td>
      <td className="p-2 align-middle text-end text-muted">
        {data?.submited_at ? convertDateWithTime(data?.submited_at) : '-'}
      </td>
      <td className="p-2 align-middle text-end">
        <TableAction>
          <Button
            variant="light"
            className="btn-sm border rounded text-secondary me-2"
            style={{ width: '32px', height: '32px', padding: 0 }}
            onClick={() =>
              onHandleShowModalUploadDocument({
                inboundId: data.inbound_id,
                code: data.inbound_code
              })
            }
          >
            <Upload size={13} />
          </Button>
          <Button
            variant="light"
            className="btn-sm border rounded text-secondary"
            style={{ width: '32px', height: '32px', padding: 0 }}
            onClick={() =>
              navigate(
                urlDetail(
                  data?.type,
                  data?.pr_id,
                  data?.ml_id,
                  data?.ir_id,
                  data?.mr_id
                ),
                {
                  replace: true,
                  state: { ...stateValue, source: 'inbound-history' }
                }
              )
            }
          >
            <Search size={13} />
          </Button>
        </TableAction>
      </td>
    </tr>
  )
}

export default InboundHistoryPage
